import React, { useEffect } from "react";
import styled from "styled-components";
import { animateScroll as scroll } from "react-scroll";
import Testimonial from "../components/Sections/Testimonial";
import DemoCredentialButton from "../components/Elements/DemoCredentialButton";
import { APP_CREDENTIALS } from "../config/demoCredentials";
import { 
  MdOutlineAnalytics, 
  MdOutlineLanguage, 
  MdOutlineVideoLibrary, 
  MdOutlineFactCheck,
  MdOutlineCloudSync,
  MdOutlineLock
} from "react-icons/md";

const WalkthroughTrainingSystemPage = () => {
  useEffect(() => {
    scroll.scrollToTop();
  }, []);

  const handleTryDemo = () => {
    alert("Dummy Project Demo!");
  };

  const features = [
    {
      icon: <MdOutlineAnalytics fontSize={26} />,
      title: "Strict Microlearning Enforcement",
      description: "Prompt engineering constraints cap narration at ~280 words / 2 minutes max to eliminate cognitive overload.",
    },
    {
      icon: <MdOutlineLanguage fontSize={26} />,
      title: "1-to-1 Aligned Multilingual Quizzes",
      description: "Strict JSON schema guarantees standardized evaluation across multilingual workforces regardless of language spoken.",
    },
    {
      icon: <MdOutlineVideoLibrary fontSize={26} />,
      title: "Photorealistic Backdrop Integration",
      description: "DALL-E generates people-free industrial backgrounds while HeyGen removes avatar green screens for studio-quality video.",
    },
    {
      icon: <MdOutlineFactCheck fontSize={26} />,
      title: "Human-in-the-Loop Draft Continuity",
      description: "Guarantees subject matter experts have full editorial control before any video is rendered via a persistent drafts table.",
    },
    {
      icon: <MdOutlineCloudSync fontSize={26} />,
      title: "Fault-Tolerant Async Video Queue",
      description: "Eliminates dropped video generation jobs using dual-channel status tracking via webhooks and fallback polling.",
    },
    {
      icon: <MdOutlineLock fontSize={26} />,
      title: "Sequential Progression Lock",
      description: "Ensures operators master basic safety before handling complex equipment, driven by a global app setting toggle.",
    },
  ];

  return (
    <>
      <Wrapper className="flexNullCenter flexColumn">
        <HeroContainer>
          <ContentWrapper>
            <TextContent>
              <Title>Walkthrough Training System</Title>
              <Description>
                Transforming dense machinery manuals and safety SOPs into photorealistic, multilingual AI video walkthroughs and interactive assessments in under 5 minutes.
              </Description>
              <ButtonWrapper>
                <DemoCredentialButton credentials={APP_CREDENTIALS.walkthroughTraining} />
                <TryButton onClick={handleTryDemo}>Try it</TryButton>
              </ButtonWrapper>
            </TextContent>
            <InfoSection>
              <p>
                Industrial training is broken: workers are handed 50-page paper manuals in languages they might not comfortably speak, leading to machine downtime and workplace accidents. Traditional training video production costs thousands of dollars and takes weeks.
              </p>
              <p>
                The Walkthrough Training System replaces this with an AI-powered pipeline: supervisors simply upload a machine manual or safety SOP, and the platform automatically extracts the core procedures, generates a photorealistic industrial backdrop, renders a human-like AI avatar presenter speaking in the worker's native language, and builds an aligned 5-question assessment—all in minutes.
              </p>
              <VideoContainer>
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/YIfW11JZRLk?autoplay=0"
                  title="Demo Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  frameBorder="0"
                />
              </VideoContainer>
              <p>
                By delivering visual walkthroughs presented in the operator's native language with instant comprehension verification, we turn static compliance documents into active workplace protection.
              </p>
            </InfoSection>
          </ContentWrapper>
        </HeroContainer>
      </Wrapper>

      <DemoSampleWrapper className="lightBg">
        <div className="container">
          <HeaderInfo>
            <h1 className="font40 extraBold">Features</h1>
            <p className="font13">
              Dummy features overview.
            </p>
          </HeaderInfo>
          <DemoSampleWrapperDiv>
            {features.map((feature, index) => (
              <FeatureWrapper
                key={index}
                className="whiteBg radius8 shadow"
              >
                <DemoFeatureDiv>
                  <DemoFeatureIcon>{feature.icon}</DemoFeatureIcon>
                  <DemoFeatureTitle className="font20 extraBold">
                    {feature.title}
                  </DemoFeatureTitle>
                </DemoFeatureDiv>
                <DemoFeatureDesc className="font13">
                  {feature.description}
                </DemoFeatureDesc>
              </FeatureWrapper>
            ))}
          </DemoSampleWrapperDiv>
        </div>
      </DemoSampleWrapper>

      <Testimonial />
    </>
  );
};

const Wrapper = styled.section`
  width: 100%;
  max-width: 1220px;
  margin: 0 auto;
  padding: 110px 30px 0px 30px;
  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

const HeroContainer = styled.section`
  background-color: #f9fafb;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 32px;
  width: 100%;
`;

const InfoSection = styled.div`
  width: 100%;
  margin-top: 10px;
  padding: 20px;
  text-align: left;
  
  p {
    font-size: 16px;
    line-height: 1.6;
    color: #4b5563;
    margin-bottom: 20px;
  }
`;

const VideoContainer = styled.div`
  width: 100%;
  height: 500px;
  margin: 30px 0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0px 10px 20px -3px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 32px;

  @media (max-width: 1024px) {
    flex-direction: column;
    text-align: center;
  }
`;

const TextContent = styled.div`
  flex: 1;
  min-width: 300px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
`;

const Description = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin-bottom: 0;
  line-height: 2;
`;

const DemoSampleWrapper = styled.div`
  margin-top: 50px;
  padding: 50px 0px;
`;

const HeaderInfo = styled.div`
  margin-bottom: 30px;
  @media (max-width: 860px) {
    text-align: center;
  }
`;

const DemoSampleWrapperDiv = styled.div`
  width: 100%;
  max-width: 1220px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const FeatureWrapper = styled.div`
  width: 350px;
  height: 220px;
  text-align: left;
  padding: 20px 30px;
  margin: 30px 0px;
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: scale(1.05) translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  }
`;

const DemoFeatureDiv = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const DemoFeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
`;

const DemoFeatureTitle = styled.h3`
  width: 70%;
  padding: 10px 15px;
`;

const DemoFeatureDesc = styled.p`
  padding: 10px 0px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;

const TryButton = styled.button`
  background-color: #7620ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
  box-shadow: 0 4px 6px rgba(118, 32, 255, 0.2);

  &:hover {
    background-color: #6010e2;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default WalkthroughTrainingSystemPage;
